export const MAX_MAILTO_LENGTH = 1800;

export type PreparedEmail = {
  to: string;
  subject: string;
  body: string;
};

export function preparedEmailHref(email: PreparedEmail) {
  return `mailto:${encodeURIComponent(email.to)}?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
}

export function preparedEmailText(email: PreparedEmail) {
  return `A: ${email.to}\nOggetto: ${email.subject}\n\n${email.body}`;
}

/** Tempo concesso al dispositivo per aprire la posta prima di considerarlo muto. */
export const MAIL_APP_TIMEOUT_MS = 1500;

type Ascoltabile = Pick<Window, "addEventListener" | "removeEventListener">;

export type MailAppWatch = {
  win?: Ascoltabile;
  doc?: Ascoltabile & { visibilityState?: DocumentVisibilityState };
  timeoutMs?: number;
};

/**
 * Dice se il dispositivo ha davvero aperto qualcosa dopo un mailto.
 *
 * Un mailto senza applicazione di posta associata fallisce in silenzio: nessun errore,
 * nessun evento, niente. L'unica traccia di un'apertura riuscita e che l'app perde il
 * primo piano, quindi si aspetta quel segnale per un attimo e, se non arriva, si
 * risponde di no invece di lasciare l'utente davanti a uno schermo che non reagisce.
 */
export function waitForMailApp(watch: MailAppWatch = {}): Promise<boolean> {
  const {
    win = typeof window === "undefined" ? undefined : window,
    doc = typeof document === "undefined" ? undefined : document,
    timeoutMs = MAIL_APP_TIMEOUT_MS,
  } = watch;

  if (!win || !doc) return Promise.resolve(false);
  const finestra = win;
  const documento = doc;

  return new Promise<boolean>((risolvi) => {
    let chiuso = false;

    const smetti = (aperta: boolean) => {
      if (chiuso) return;
      chiuso = true;
      clearTimeout(timer);
      finestra.removeEventListener("blur", onBlur);
      finestra.removeEventListener("pagehide", onBlur);
      documento.removeEventListener("visibilitychange", onVisibility);
      risolvi(aperta);
    };

    function onBlur() {
      smetti(true);
    }

    // Il ritorno in primo piano non conta: conta soltanto l'uscita verso un'altra app.
    function onVisibility() {
      if (documento.visibilityState === "hidden") smetti(true);
    }

    const timer = setTimeout(() => smetti(false), timeoutMs);
    finestra.addEventListener("blur", onBlur);
    finestra.addEventListener("pagehide", onBlur);
    documento.addEventListener("visibilitychange", onVisibility);
  });
}

export async function openPreparedEmail(email: PreparedEmail) {
  const href = preparedEmailHref(email);
  if (href.length > MAX_MAILTO_LENGTH) {
    await navigator.clipboard.writeText(preparedEmailText(email));
    return { opened: false, copied: true };
  }
  window.location.assign(href);
  return { opened: true, copied: false };
}
